import { ChangeDetectorRef, Component, ElementRef, inject, Input, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faClockRotateLeft } from '@fortawesome/pro-light-svg-icons';
import { formatAuditDateValue } from '../../core/utils/date-only.util';

export interface FieldAuditEntry {
  fieldLabel: string;
  date: string;
  user: string;
  before: string;
  after: string;
}

@Component({
  selector: 'app-field-audit-indicator',
  standalone: true,
  imports: [CommonModule, FontAwesomeModule],
  templateUrl: './field-audit-indicator.component.html',
  styleUrl: './field-audit-indicator.component.scss',
})
export class FieldAuditIndicatorComponent implements OnDestroy {
  private cdr = inject(ChangeDetectorRef);
  private elRef = inject(ElementRef);

  @Input() audit: FieldAuditEntry | null = null;

  faClockRotateLeft = faClockRotateLeft;

  private popoverEl: HTMLDivElement | null = null;
  private outsideClickHandler = (e: MouseEvent) => this.onOutsideClick(e);

  toggle(event: Event) {
    event.stopPropagation();

    if (this.popoverEl) {
      this.destroyPopover();
      return;
    }

    if (!this.audit) return;

    const btn = (event.target as HTMLElement).closest('.audit-trigger') as HTMLElement;
    if (!btn) return;

    const rect = btn.getBoundingClientRect();

    const el = document.createElement('div');
    el.className = 'audit-floating-popover';
    el.innerHTML = this.buildContent();
    document.body.appendChild(el);
    this.popoverEl = el;

    const popRect = el.getBoundingClientRect();
    let top = rect.bottom + 6;
    let left = rect.right - popRect.width;

    if (left < 8) left = 8;
    if (top + popRect.height > window.innerHeight - 8) {
      top = rect.top - popRect.height - 6;
    }

    el.style.top = `${top}px`;
    el.style.left = `${left}px`;

    requestAnimationFrame(() => {
      el.classList.add('is-visible');
    });

    setTimeout(() => {
      document.addEventListener('click', this.outsideClickHandler, true);
    });
  }

  private onOutsideClick(e: MouseEvent) {
    if (this.popoverEl && !this.popoverEl.contains(e.target as Node) &&
        !this.elRef.nativeElement.contains(e.target as Node)) {
      this.destroyPopover();
    }
  }

  private destroyPopover() {
    document.removeEventListener('click', this.outsideClickHandler, true);
    if (this.popoverEl) {
      this.popoverEl.remove();
      this.popoverEl = null;
    }
  }

  private buildContent(): string {
    if (!this.audit) return '';
    const date = this.formatDate(this.audit.date);
    const before = this.formatFieldValue(this.audit.before);
    const after = this.formatFieldValue(this.audit.after);
    return `
      <div class="audit-floating-content">
        <span class="audit-floating-phrase">
          Modificado el <strong>${date}</strong> por <strong>${this.escapeHtml(this.audit.user)}</strong>:
          de «<span class="audit-val-before">${this.escapeHtml(before)}</span>»
          a «<span class="audit-val-after">${this.escapeHtml(after)}</span>»
        </span>
      </div>`;
  }

  private formatDate(iso: string): string {
    try {
      const d = new Date(iso.endsWith('Z') || iso.includes('+') ? iso : iso + 'Z');
      const pad = (n: number) => n.toString().padStart(2, '0');
      return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch {
      return iso;
    }
  }

  private formatFieldValue(val: string | null | undefined): string {
    if (val == null || val === '') return '—';
    // Normalize boolean-like strings to consistent display
    const lower = val.toLowerCase();
    if (lower === 'true' || lower === 'sí' || lower === 'si') return 'Sí';
    if (lower === 'false' || lower === 'no') return 'No';
    const date = formatAuditDateValue(val);
    if (date) return date;
    return val;
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  ngOnDestroy() {
    this.destroyPopover();
  }
}
